/**
 * The cart, as a panel that comes in from the side.
 *
 * It opens on three occasions and they are not the same occasion: the reader
 * asked for it, something was just added, or a quantity changed and the panel
 * is already open. The first two are how it appears; the third is why its
 * contents are a separate file on the server — the cart moves, the frame does
 * not.
 *
 * The markup for a changed cart comes back rendered. There is one drawer in
 * this theme and one place that knows how to draw it.
 */

import './index.scss';

const drawer = document.querySelector('.cart-drawer');
const opener = document.querySelector('.site-header__utility--bag');

if (drawer) {
    const contents = drawer.querySelector('[data-drawer-contents]');

    let flight = null;

    /**
     * Say whether the cart is being changed.
     *
     * The whole panel dims: every figure in it is out of date while a quantity
     * is in the air, and marking only the line that changed would be a guess
     * about which figures follow from it.
     *
     * @param {boolean} state Whether a request is in flight.
     */
    const busy = (state) => {
        if (state) {
            drawer.setAttribute('data-busy', 'true');
            contents.setAttribute('aria-busy', 'true');
        } else {
            drawer.removeAttribute('data-busy');
            contents.removeAttribute('aria-busy');
        }
    };

    /**
     * Tell everything that shows a count what it is now.
     *
     * The count in the drawer's own title sits above the part that gets
     * replaced, so it is set here; the header's badge belongs to another block
     * and hears about it as an event, so neither block knows the other.
     *
     * Only the number in either case: the sentences around it are translated
     * strings and stay where translators can see them.
     *
     * @param {number} count How many pieces are in the cart now.
     */
    const said = (count) => {
        const counter = drawer.querySelector('[data-drawer-count]');

        if (counter) {
            counter.textContent = String(count);
        }

        document.dispatchEvent(new CustomEvent('cart:changed', { detail: { count } }));
    };

    /**
     * Open the drawer, unless it is already open.
     */
    const open = () => {
        if (!drawer.open) {
            drawer.showModal();

            /* showModal() puts this dialog in the top layer, which no z-index
             * can be lifted above — so anything else covering the page has to
             * stand down rather than compete. Said out loud on the document so
             * each overlay closes itself and neither block reaches into the
             * other's markup. */
            document.dispatchEvent(
                new CustomEvent('overlay:opened', { detail: { who: 'cart-drawer' } })
            );
        }
    };

    /* The other way round: the menu is not in the top layer, so a drawer left
     * open would sit over it. Opening one closes the other, both directions. */
    document.addEventListener('overlay:opened', (event) => {
        if ('cart-drawer' !== event.detail?.who && drawer.open) {
            drawer.close();
        }
    });

    /**
     * Ask the shop to change the cart, and draw what it comes back as.
     *
     * @param {Object} what The job, the line and any new quantity.
     */
    const change = async (what) => {
        if (flight) {
            flight.abort();
        }

        flight = new AbortController();
        busy(true);

        try {
            const response = await fetch(drawer.dataset.endpoint, {
                method: 'POST',
                credentials: 'same-origin',
                signal: flight.signal,
                headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
                body: new URLSearchParams({
                    action: 'theme_cart_drawer',
                    nonce: drawer.dataset.nonce,
                    ...what,
                }).toString(),
            });

            const answer = await response.json();

            if (!answer?.success) {
                return;
            }

            contents.innerHTML = answer.data.html;
            said(answer.data.count);
        } catch (error) {
            if (error.name !== 'AbortError') {
                window.location.reload();
            }
        } finally {
            busy(false);
        }
    };

    /*
     * Adding a piece, without the page going anywhere.
     *
     * The form is a real form and posts on its own with the script blocked.
     * With the script running there is nothing for a page load to do: the shop
     * has to hear about the addition, and the reader has to see the drawer —
     * neither of which needs them thrown back to the top of a page they had
     * scrolled halfway down.
     *
     * If the shop refuses — an unchosen variation, a piece that sold out while
     * the page was open — the form is posted for real, so the reasons are shown
     * on a page where they can be acted on rather than swallowed here.
     */
    const forms = document.querySelectorAll('[data-cart-form]');

    forms.forEach((form) => {
        form.addEventListener('submit', async (event) => {
            event.preventDefault();

            const fields = new FormData(form);
            const body = new URLSearchParams({
                action: 'theme_cart_add',
                nonce: drawer.dataset.nonce,
                product_id: fields.get('add-to-cart') || '',
                variation_id: fields.get('variation_id') || '',
                quantity: fields.get('quantity') || '1',
            });

            /* The chosen attributes, under the names WooCommerce knows them by.
             * Everything else on the form is the page's own business. */
            fields.forEach((value, name) => {
                if (name.startsWith('attribute_')) {
                    body.append(`attributes[${name}]`, value);
                }
            });

            busy(true);

            try {
                const response = await fetch(drawer.dataset.endpoint, {
                    method: 'POST',
                    credentials: 'same-origin',
                    headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
                    body: body.toString(),
                });

                const answer = await response.json();

                if (!answer?.success) {
                    /*
                     * The shop said no. Announced rather than acted on: the
                     * page that owns the button knows where to put the reason,
                     * and this block does not. If nobody is listening the event
                     * is not cancelled and the form is posted for real, so the
                     * reasons are never simply swallowed.
                     */
                    const heard = !document.dispatchEvent(
                        new CustomEvent('cart:refused', {
                            cancelable: true,
                            detail: { form, messages: answer?.data?.messages ?? [] },
                        })
                    );

                    if (!heard) {
                        form.submit();
                    }

                    return;
                }

                contents.innerHTML = answer.data.html;
                said(answer.data.count);
                open();
            } catch {
                /* The shop could not be reached. Post the form for real: the
                 * page load will either work or say why. */
                form.submit();
            } finally {
                busy(false);
            }
        });
    });

    /* The bag in the header opens the drawer instead of going to the cart page.
     * It stays a link: with the script blocked it still goes somewhere. */
    if (opener) {
        opener.addEventListener('click', (event) => {
            event.preventDefault();
            open();
        });
    }

    drawer.addEventListener('click', (event) => {
        if (event.target.closest('[data-drawer-close]')) {
            drawer.close();
            return;
        }

        /* A click on the dialog itself is a click on the scrim: everything the
         * drawer draws lives inside `__panel`. */
        if (event.target === drawer) {
            drawer.close();
            return;
        }

        const line = event.target.closest('.drawer-line');

        if (!line) {
            return;
        }

        if (event.target.closest('[data-remove]')) {
            change({ job: 'remove', key: line.dataset.key });
            return;
        }

        const step = event.target.closest('[data-step]');

        if (step) {
            const now = parseInt(line.querySelector('[data-quantity]').textContent.trim(), 10) || 1;

            change({
                job: 'quantity',
                key: line.dataset.key,
                quantity: String(now + parseInt(step.dataset.step, 10)),
            });
        }
    });

    drawer.addEventListener('close', () => {
        if (opener) {
            opener.focus();
        }
    });

    /*
     * Something went in on the request that drew this page. The server said so
     * with an attribute rather than the script asking, because by the time
     * anything here runs the POST that added it is two pages ago.
     */
    if (drawer.hasAttribute('data-open-now')) {
        open();
    }

    /*
     * And for the shop's simple pieces, which WooCommerce adds without leaving
     * the page. That announcement is on jQuery's bus because WooCommerce's
     * script is; this is the theme calling its API, not adopting a library.
     */
    if (window.jQuery) {
        window.jQuery(document.body).on('added_to_cart', () => {
            change({ job: 'show' }).then(open);
        });
    }
}
